import React from 'react'
import { observer } from 'mobx-react-lite'
import { Divider, Form, Modal, Row, Select, Space } from 'antd-v4'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { ILocationZoneContract, LOCATION_ZONE_OPTION } from '@getgreenline/blaze-ecomm'
import { ConfigureEcomDelivery } from './ConfigureEcomDelivery'
import { ConfigureEcomDeliveryComponent } from './ConfigureEcomDeliveryComponent'
import KMLUploader from './KMLUploader'
import { PolygonData } from './EcommerceSettingModels'
import { isEmpty } from 'lodash'

interface Props {
  configureEcomDelivery: ConfigureEcomDelivery
  flatRateFee: number
  updateFlatRateFee: (flatRateFee: number) => void
}

export const ConfigureEcomDeliveryDropDownForm = observer(
  ({ configureEcomDelivery, flatRateFee, updateFlatRateFee }: Props) => {
    const isPostalCodeDisabled = React.useMemo(
      () => !configureEcomDelivery.isPostalCodeOptionEnabled,
      [configureEcomDelivery.isPostalCodeOptionEnabled],
    )

    const isKMLDisabled = React.useMemo(
      () => !configureEcomDelivery.isKMLOptionEnabled,
      [configureEcomDelivery.isKMLOptionEnabled],
    )

    const getLocationZoneOptionLabel = (option: LOCATION_ZONE_OPTION) => {
      switch (option) {
        case LOCATION_ZONE_OPTION.FLAT_RATE:
          return 'Flat rate'
        case LOCATION_ZONE_OPTION.POSTAL_CODE:
          return 'Postal Codes'
        case LOCATION_ZONE_OPTION.KML:
          return 'KML'
      }
    }

    const setLocationZoneOption = (newOption: LOCATION_ZONE_OPTION) => {
      let newLocationZones: ILocationZoneContract[] = []

      if (newOption === LOCATION_ZONE_OPTION.FLAT_RATE) {
        newLocationZones = configureEcomDelivery.addLocationZone({
          locationZoneOption: LOCATION_ZONE_OPTION.FLAT_RATE,
          name: configureEcomDelivery.FLAT_RATE_NAME,
          postalCodes: ['*'],
        })
      }

      updateFlatRateFee(0)
      configureEcomDelivery.setActiveKeys([])
      configureEcomDelivery.setLocationZoneOption(newOption)
      configureEcomDelivery.setAllLocationZones(newLocationZones)
    }

    const changeLocationZoneOption = (newOption: LOCATION_ZONE_OPTION) => {
      const hasZoneWithCurrentOption = (configureEcomDelivery.locationZonesToShow || []).some(
        (lz) => lz.locationZoneOption !== newOption,
      )
      const hasFeeWithFlatRateOption =
        configureEcomDelivery.isFlatRateOptionEnabled && flatRateFee > 0

      if (hasZoneWithCurrentOption || hasFeeWithFlatRateOption) {
        Modal.confirm({
          title: 'Replacing Existing Zones',
          icon: <ExclamationCircleOutlined translate='yes' />,
          zIndex: 1002,
          width: 'fit-content',
          content: (
            <div className='text-danger'>
              Changing to a different type will <strong>permanently</strong> remove all your current
              zones. <br />
              Are you sure you want to change it?
            </div>
          ),
          okText: 'Yes',
          okButtonProps: { type: 'primary', danger: true },
          cancelText: 'Cancel',
          centered: true,
          onOk: () => setLocationZoneOption(newOption),
        })
      } else {
        setLocationZoneOption(newOption)
      }
    }

    const confirmAndUploadKML = (polygons: PolygonData[]) => {
      if (isEmpty(configureEcomDelivery.locationZonesToShow)) {
        return configureEcomDelivery.uploadMultipleKMLZones(polygons)
      }

      Modal.confirm({
        title: 'Replacing Existing Zones',
        icon: <ExclamationCircleOutlined translate='yes' />,
        zIndex: 1002,
        width: 'fit-content',
        content: (
          <div className='text-danger'>
            Uploading a KML file with multiple zones will <strong>permanently</strong> replace your
            existing zones. <br />
            Are you sure you want to replace existing zones?
          </div>
        ),
        okText: 'Upload',
        okButtonProps: { type: 'primary', danger: true },
        cancelText: 'Cancel',
        centered: true,
        onOk: () => configureEcomDelivery.uploadMultipleKMLZones(polygons),
      })
    }

    return (
      <>
        <Row style={{ display: 'flex', alignItems: 'center' }}>
          <Select<LOCATION_ZONE_OPTION>
            style={{ width: 200 }}
            value={configureEcomDelivery.locationZoneOption}
            onChange={changeLocationZoneOption}
          >
            {Object.values(LOCATION_ZONE_OPTION).map((option) => (
              <Select.Option key={option} value={option}>
                {getLocationZoneOptionLabel(option)}
              </Select.Option>
            ))}
          </Select>

          {configureEcomDelivery.isKMLOptionEnabled && (
            <div style={{ marginLeft: 100 }}>
              <KMLUploader upload={confirmAndUploadKML} />
            </div>
          )}
        </Row>

        <Divider />

        {configureEcomDelivery.isFlatRateOptionEnabled && (
          <Form>
            <ConfigureEcomDeliveryComponent.FlatRate
              flatRateFee={flatRateFee}
              updateFlatRateFee={updateFlatRateFee}
              disabled={!configureEcomDelivery.isFlatRateOptionEnabled}
            />
          </Form>
        )}

        {configureEcomDelivery.isPostalCodeOptionEnabled && (
          <Space direction='vertical'>
            <Row style={{ display: 'flex', justifyContent: 'end' }}>
              <ConfigureEcomDeliveryComponent.LocationZoneToolbar
                addLocationZone={configureEcomDelivery.addLocationZone}
                activeKeys={configureEcomDelivery.activeKeys}
                toggleActiveKeys={configureEcomDelivery.toggleActiveKeys}
                disabled={isPostalCodeDisabled}
              />
            </Row>
            <Row>
              {configureEcomDelivery.locationZonesToShow?.map((locationZone, index) => {
                return (
                  <ConfigureEcomDeliveryComponent.PostalCode
                    key={index}
                    index={index}
                    locationZone={locationZone}
                    configureEcomDelivery={configureEcomDelivery}
                    disabled={isPostalCodeDisabled}
                  />
                )
              })}
            </Row>
          </Space>
        )}

        {configureEcomDelivery.isKMLOptionEnabled && (
          <Space direction='vertical'>
            <Row style={{ display: 'flex', justifyContent: 'end' }}>
              <ConfigureEcomDeliveryComponent.LocationZoneToolbar
                addLocationZone={configureEcomDelivery.addLocationZone}
                activeKeys={configureEcomDelivery.activeKeys}
                toggleActiveKeys={configureEcomDelivery.toggleActiveKeys}
                disabled={isKMLDisabled}
              />
            </Row>

            <Row>
              {configureEcomDelivery.locationZonesToShow?.map((locationZone, index) => {
                return (
                  <ConfigureEcomDeliveryComponent.KML
                    key={index}
                    index={index}
                    locationZone={locationZone}
                    configureEcomDelivery={configureEcomDelivery}
                    disabled={isKMLDisabled}
                  />
                )
              })}
            </Row>
          </Space>
        )}
      </>
    )
  },
)
